import { useState } from 'react';
import { Table, message, Modal, Radio, Space, Descriptions } from 'antd';
import type { RadioChangeEvent } from 'antd';
import {
  ContentCard,
  useSignupUsers,
  getSignupColumns,
} from '../../component/admin';
import type { SignupUsersData } from '../../component/admin';
import { renderSpecialist } from '../../component/admin/config/columns/signupColumns';
import { approveUser, rejectUser } from '../../api/adminAPI';

const SignupManagePage = () => {
  const { users, loading, error, refetch } = useSignupUsers();

  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [targetUserId, setTargetUserId] = useState<number | null>(null);
  // 희망 유형이 없으므로 미리 고르지 않고, 관리자가 신청 정보를 보고 직접 고른다
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailUser, setDetailUser] = useState<SignupUsersData | null>(null);

  const handleOpenApproveModal = (userId: number) => {
    setTargetUserId(userId);
    setSelectedRole(null);
    setIsApproveOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (targetUserId === null || !selectedRole) return;
    try {
      await approveUser(targetUserId, selectedRole);
      message.success('승인되었습니다.');
      setIsApproveOpen(false);
      refetch();
    } catch (e) {
      console.error(e);
      message.error('승인 처리에 실패했습니다.');
    }
  };

  const handleReject = async (userId: number) => {
    try {
      await rejectUser(userId);
      message.success('거절되었습니다.');
      refetch();
    } catch (e) {
      message.error('거절 실패');
    }
  };

  const handleOpenDetail = (record: SignupUsersData) => {
    setDetailUser(record);
    setIsDetailOpen(true);
  };

  const handleApproveFromDetail = () => {
    if (!detailUser) return;
    setIsDetailOpen(false);
    handleOpenApproveModal(detailUser.userId);
  };

  const onRoleChange = (e: RadioChangeEvent) => {
    setSelectedRole(e.target.value);
  };

  const columns = getSignupColumns(
    handleOpenApproveModal,
    handleReject,
    handleOpenDetail,
  );

  if (loading) return <div>loading...</div>;
  if (error) return <div>error: {error}</div>;

  const targetUserInfo = users.find(u => u.userId === targetUserId);

  return (
    <>
      <ContentCard title={`회원가입 관리 (대기 ${users.length}건)`}>
        <Table
          rowKey={'userId'}
          columns={columns}
          dataSource={users}
          scroll={{ x: 'max-content' }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total}`,
          }}
        />
      </ContentCard>

      <Modal
        title="회원 가입 승인"
        open={isApproveOpen}
        onOk={handleConfirmApprove}
        onCancel={() => setIsApproveOpen(false)}
        okText="승인"
        cancelText="취소"
        okButtonProps={{ disabled: !selectedRole }}
        centered
      >
        {targetUserInfo && (
          <div
            style={{
              padding: '12px 16px',
              background: '#f6f8fb',
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 13,
            }}
          >
            <div><strong>{targetUserInfo.name}</strong> ({targetUserInfo.username})</div>
            <div style={{ color: '#888', marginTop: 4 }}>
              {[targetUserInfo.workplace, targetUserInfo.department, targetUserInfo.position]
                .filter(Boolean)
                .join(' · ') || '-'}
            </div>
            <div style={{ color: '#888', marginTop: 4 }}>
              면허 {targetUserInfo.medicalLicenseNumber || '없음'}
              <span style={{ margin: '0 6px' }}>·</span>
              전문의 {renderSpecialist(targetUserInfo)}
            </div>
          </div>
        )}
        <div style={{ padding: '8px 0' }}>
          <p style={{ marginBottom: '10px' }}>부여할 권한을 선택해주세요:</p>
          <Radio.Group onChange={onRoleChange} value={selectedRole}>
            <Space direction="vertical">
              <Radio value="REGULAR">정회원</Radio>
              <Radio value="GENERAL">일반회원</Radio>
              <Radio value="STUDENT">학생회원</Radio>
              <Radio value="OTHER">기타회원</Radio>
            </Space>
          </Radio.Group>
        </div>
      </Modal>

      <Modal
        title="가입 신청 상세 정보"
        open={isDetailOpen}
        onCancel={() => setIsDetailOpen(false)}
        centered
        width={560}
        footer={[
          <button
            key="close"
            onClick={() => setIsDetailOpen(false)}
            style={btnStyle('default')}
          >
            닫기
          </button>,
          <button
            key="approve"
            onClick={handleApproveFromDetail}
            style={btnStyle('primary')}
          >
            승인 진행
          </button>,
        ]}
      >
        {detailUser && (
          <Descriptions
            bordered
            column={1}
            size="small"
            labelStyle={{ width: 120, fontWeight: 600, background: '#fafafa' }}
          >
            <Descriptions.Item label="아이디">{detailUser.username}</Descriptions.Item>
            <Descriptions.Item label="이름">{detailUser.name}</Descriptions.Item>
            <Descriptions.Item label="생년월일">{detailUser.birthDate || '-'}</Descriptions.Item>
            <Descriptions.Item label="이메일">{detailUser.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="휴대폰">{detailUser.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="직장 / 학교">{detailUser.workplace || '-'}</Descriptions.Item>
            <Descriptions.Item label="소속과">{detailUser.department || '-'}</Descriptions.Item>
            <Descriptions.Item label="직위">{detailUser.position || '-'}</Descriptions.Item>
            <Descriptions.Item label="의사면허번호">
              {detailUser.medicalLicenseNumber || (
                <span style={{ color: '#bbb' }}>없음</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="전문의">{renderSpecialist(detailUser)}</Descriptions.Item>
            <Descriptions.Item label="신청일">
              {detailUser.createdAt?.substring(0, 10) ?? '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

const btnStyle = (variant: 'default' | 'primary'): React.CSSProperties => ({
  padding: '6px 16px',
  marginLeft: 8,
  borderRadius: 6,
  border: variant === 'primary' ? 'none' : '1px solid #d9d9d9',
  background: variant === 'primary' ? '#1677ff' : '#fff',
  color: variant === 'primary' ? '#fff' : '#333',
  cursor: 'pointer',
  fontSize: 14,
});

export default SignupManagePage;